import { useState } from "react";

import { textFieldContainerVariants, textFieldInputClass } from "../../atoms/text-field";
import { Typography } from "../../atoms/typography";
import { cn } from "../../lib";
import { Box, Pressable, ScrollView, TextInput } from "../../primitives";
import { BottomSheet, type BottomSheetDetents } from "../bottom-sheet";
import { CountryRowContent } from "./country-row";
import { countryRowSelectedClassName, filterCountries } from "./lib";
import type { PhoneNumberFieldCountry } from "./types";

type CountrySheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  countries: PhoneNumberFieldCountry[];
  selectedCode: string;
  onSelect: (country: PhoneNumberFieldCountry) => void;
  /** Native rests at 40% and expands; web opens at one detent */
  detents: BottomSheetDetents;
  /** Native: drags stay on the handle, so the list owns vertical scroll */
  handleOnly?: boolean;
};

/**
 * The country picker as a bottom sheet: a title, a search field, the list and
 * a no-match line. iOS and Android use it, and so does web below 768 wide
 * (Ardhi's DQ25 answer, 2026-11-02); wider web keeps the dropdown.
 */
export function CountrySheet({
  open,
  onOpenChange,
  countries,
  selectedCode,
  onSelect,
  detents,
  handleOnly = false,
}: CountrySheetProps) {
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const filteredCountries = filterCountries(countries, search);

  // A sheet forgets its search as it closes, dismissed or picked from; its
  // input may not get a blur as it goes, so the focused look goes too
  const reset = () => {
    setSearch("");
    setSearchFocused(false);
  };
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  return (
    <BottomSheet.Root
      open={open}
      onOpenChange={handleOpenChange}
      detents={detents}
      handleOnly={handleOnly}
    >
      <BottomSheet.Header>
        <BottomSheet.Title>Select country</BottomSheet.Title>
        {/* TextField's focused look, not the browser's outline on web */}
        <Box
          className={cn(
            textFieldContainerVariants({
              size: "sm",
              visualState: searchFocused ? "focused" : "default",
            }),
            "mt-spacing-sm",
          )}
        >
          <TextInput
            value={search}
            onChangeText={setSearch}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Search country or code"
            accessibilityLabel="Search countries"
            className={cn(
              "text-body-md md:text-body-md-dt native:font-inter-regular h-full flex-1 bg-transparent outline-none",
              textFieldInputClass.default,
            )}
          />
        </Box>
      </BottomSheet.Header>
      <ScrollView className="flex-1" contentContainerClassName="px-spacing-sm pb-spacing-sm">
        {filteredCountries.length === 0 ? (
          <Box className="px-spacing-lg py-spacing-xl">
            <Typography variant="body-sm" color="neutral-foreground-intermediate">
              No countries match “{search}”.
            </Typography>
          </Box>
        ) : null}
        {filteredCountries.map((option) => {
          const isSelected = option.code === selectedCode;
          return (
            <Pressable
              key={option.code}
              onClick={() => {
                reset();
                onSelect(option);
              }}
              accessibilityRole="button"
              accessibilityLabel={`${option.name}, ${option.dialCode}`}
              accessibilityState={{ selected: isSelected }}
              className={cn(
                "rounded-radius-md px-spacing-lg py-spacing-md flex flex-row items-center justify-between",
                isSelected && countryRowSelectedClassName,
              )}
            >
              <CountryRowContent country={option} selected={isSelected} />
            </Pressable>
          );
        })}
      </ScrollView>
    </BottomSheet.Root>
  );
}
